/* ---------- wordmark ---------- */
.wordmark {
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1;
  color: var(--text);
}
.wordmarkAccent {
  color: var(--accent);
}

/* ---------- buttons ---------- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: var(--r-full);
  font-weight: 700;
  letter-spacing: -0.01em;
  transition: transform var(--t-fast) var(--ease), background var(--t-fast), opacity var(--t-fast), border-color var(--t-fast);
  white-space: nowrap;
  user-select: none;
}
.btn:active:not(:disabled) {
  transform: scale(0.97);
}
.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.btn_sm {
  height: 32px;
  padding: 0 14px;
  font-size: var(--fs-sm);
}
.btn_md {
  height: 42px;
  padding: 0 20px;
  font-size: var(--fs-md);
}
.btn_lg {
  height: 52px;
  padding: 0 26px;
  font-size: var(--fs-md);
}
.btn_primary {
  background: var(--accent);
  color: var(--accent-ink);
}
.btn_primary:hover:not(:disabled) {
  background: color-mix(in srgb, var(--accent) 88%, white);
}
.btn_secondary {
  background: var(--surface-3);
  color: var(--text);
}
.btn_secondary:hover:not(:disabled) {
  background: var(--surface-4);
}
.btn_outline {
  border: 1px solid var(--border-strong);
  color: var(--text);
}
.btn_outline:hover:not(:disabled) {
  border-color: var(--text-3);
}
.btn_ghost {
  color: var(--text-2);
}
.btn_ghost:hover:not(:disabled) {
  color: var(--text);
  background: var(--surface-2);
}
.btn_danger {
  background: rgb(255 77 94 / 0.14);
  color: var(--danger);
}
.block {
  width: 100%;
}

.iconBtn {
  display: inline-grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: var(--r-full);
  color: var(--text);
  transition: background var(--t-fast), color var(--t-fast), transform var(--t-fast) var(--ease);
  flex-shrink: 0;
}
.iconBtn:hover {
  background: rgb(255 255 255 / 0.08);
}
.iconBtn:active {
  transform: scale(0.92);
}
.iconBtnActive {
  color: var(--accent);
}

/* ---------- avatar ---------- */
.avatar {
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, var(--surface-4), var(--surface-2));
  font-weight: 800;
  color: var(--text-2);
}
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avatarRing {
  box-shadow: 0 0 0 2px var(--bg), 0 0 0 4px var(--accent);
}
.verified {
  display: inline-flex;
  color: var(--accent);
  vertical-align: -2px;
}

/* ---------- feedback ---------- */
.spinner {
  display: inline-block;
  border-radius: 50%;
  border: 2.5px solid rgb(255 255 255 / 0.18);
  border-top-color: currentColor;
  animation: bb-spin 0.7s linear infinite;
}
.skeleton {
  display: block;
  background: linear-gradient(90deg, var(--surface-2) 25%, var(--surface-3) 50%, var(--surface-2) 75%);
  background-size: 200% 100%;
  animation: bb-shimmer 1.4s infinite linear;
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 56px 24px;
  color: var(--text-2);
  animation: bb-fade-in var(--t-med) var(--ease);
}
.empty h3 {
  color: var(--text);
  font-size: var(--fs-lg);
  font-weight: 700;
}
.empty p {
  max-width: 280px;
  font-size: var(--fs-sm);
  line-height: 1.5;
  margin-bottom: 8px;
}
.emptyIcon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent);
  margin-bottom: 6px;
}
.loadMore {
  display: grid;
  place-items: center;
  min-height: 64px;
  color: var(--text-3);
}

/* ---------- fields ---------- */
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fieldLabel {
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text-2);
}
.input {
  height: 48px;
  padding: 0 14px;
  border-radius: var(--r-md);
  background: var(--surface-2);
  border: 1px solid var(--border);
  outline: none;
  font-size: var(--fs-md);
  transition: border-color var(--t-fast), background var(--t-fast);
}
.input::placeholder {
  color: var(--text-3);
}
.input:focus {
  border-color: var(--accent);
  background: var(--surface-1);
}
.textarea {
  height: auto;
  min-height: 96px;
  padding: 12px 14px;
  resize: vertical;
  line-height: 1.45;
}
.inputError {
  border-color: var(--danger);
}
.fieldError {
  font-size: var(--fs-xs);
  color: var(--danger);
}
.fieldHint {
  font-size: var(--fs-xs);
  color: var(--text-3);
}

.switchRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
}
.switchText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-weight: 600;
  font-size: var(--fs-md);
}
.switchText small {
  color: var(--text-3);
  font-weight: 500;
  font-size: var(--fs-xs);
}
.switch {
  position: relative;
  width: 50px;
  height: 30px;
  border-radius: var(--r-full);
  background: var(--surface-4);
  transition: background var(--t-med) var(--ease);
  flex-shrink: 0;
}
.switch span {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  transition: transform var(--t-med) var(--ease-spring);
}
.switchOn {
  background: var(--accent);
}
.switchOn span {
  transform: translateX(20px);
}

/* ---------- tabs & chips ---------- */
.tabs {
  display: flex;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}
.tabsSticky {
  position: sticky;
  top: 0;
  z-index: 5;
}
.tab {
  flex: 1;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  height: 46px;
  font-weight: 700;
  font-size: var(--fs-sm);
  color: var(--text-3);
  position: relative;
  transition: color var(--t-fast);
}
.tab::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 30%;
  right: 30%;
  height: 2px;
  border-radius: 2px;
  background: var(--text);
  transform: scaleX(0);
  transition: transform var(--t-med) var(--ease);
}
.tabActive {
  color: var(--text);
}
.tabActive::after {
  transform: scaleX(1);
}

.chips {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 2px var(--gutter);
  scrollbar-width: none;
}
.chips::-webkit-scrollbar {
  display: none;
}
.chip {
  height: 34px;
  padding: 0 14px;
  border-radius: var(--r-full);
  background: var(--surface-2);
  color: var(--text-2);
  font-size: var(--fs-sm);
  font-weight: 600;
  white-space: nowrap;
  transition: background var(--t-fast), color var(--t-fast);
}
.chipActive {
  background: var(--text);
  color: var(--bg);
}
